/* Лента событий — общая для дашборда и карточки клиента (ui.js events/feedHTML).
   Ответ на вопрос клиента — прямо в ленте, без перехода в карточку. */
import { trpc, useCatalog } from '@tg/app-core';
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useClients, type FeedItem } from '../lib/data';
import { PMNAMES, planOf } from '../lib/dict';
import { addDays, ago, dayLabel, daysBetween, fmtNum, grpIni, humanDate, ini, plural, rub, today } from '../lib/format';
import { ICON, type IconName } from './icons';
import { toast } from './Toast';

/** Напоминание об оплате подписки (due) показывается как событие «Подписка» */
export type EvType = Exclude<FeedItem['t'], 'due'>;
export const TYPES: Record<EvType, string> = { q: 'Вопросы', qa: 'Отвеченные', pr: 'Рекорды', miss: 'Пропуски', prog: 'Составление', new: 'Новые клиенты', start: 'Первые шаги', done: 'Результаты', pay: 'Подписка' };
const TLABEL: Record<EvType, string> = { pr: 'Рекорд', q: 'Вопрос', qa: 'Отвечено', miss: 'Пропуск', prog: 'Кончаются тренировки', new: 'Новый клиент', start: 'Первые шаги', done: 'Результат', pay: 'Подписка' };
const TICON: Record<EvType, IconName> = { pr: 'star', q: 'chat', qa: 'chat', miss: 'clock', prog: 'prog', new: 'users', start: 'chk', done: 'chk', pay: 'folder' };

export interface Ev {
  key: string;
  t: EvType;
  d: string;
  ini: string | null;
  /** Фото клиента — вместо инициалов */
  photo?: string | null;
  /** Группа: аватар — значок группы */
  grp?: boolean;
  /** Куда ведут фото и имя: карточка клиента или группа */
  who?: string | null;
  title: string;
  /** Упражнение или блок вопроса — жирным перед текстом */
  sub?: string | null;
  /** Вопрос: блок, упражнение и день — ссылкой на тренировку в конструкторе */
  qref?: { label: string; href: string } | null;
  tx: ReactNode;
  /** Текст события для счётчика «новых», когда tx — разметка */
  txt?: string;
  act: string;
  href: string;
  src: FeedItem;
}

/** Строки ленты из ответа API: подписи, ссылки, действия */
export function useEvents(items: FeedItem[] | undefined): Ev[] {
  const cs = useClients().data;
  const cat = useCatalog();
  if (!items) return [];
  const cl = (id: string) => cs?.find((c) => c.id === id);
  const pmLabel = (k: string) => PMNAMES[k] || cat.ex(k)?.ru || k;
  const T = today();
  return items.map((x, i): Ev => {
    const key = x.t + ':' + i;
    if (x.t === 'pay') return { key, t: x.t, d: x.d, ini: null, title: 'Подписка продлена', tx: `${rub(x.amount)} списаны · следующий платёж через месяц`, act: 'Подписка', href: '/profile#billing', src: x };
    if (x.t === 'due') {
      const what = x.trial ? 'Пробный период' : 'Подписка';
      if (x.left < 0)
        return { key, t: 'pay', d: x.d, ini: null, title: `${what} ${x.trial ? 'закончился' : 'закончилась'}`, tx: 'Кабинет только для просмотра — продлите подписку, чтобы снова составлять тренировки и добавлять клиентов', act: 'Продлить подписку', href: '/profile#billing', src: x };
      const nx = x.next ? planOf(x.next) : null;
      const when = x.left === 1 ? 'завтра' : `через ${x.left} ${plural(x.left, 'день', 'дня', 'дней')}`;
      return {
        key,
        t: 'pay',
        d: x.d,
        ini: null,
        title: `${what} заканчивается ${when}`,
        tx: x.trial
          ? nx
            ? `Оплатите ${rub(nx.price)} за месяц (${nx.n}) — платная подписка начнётся сразу после пробного периода`
            : 'Выберите тариф и оплатите — платная подписка начнётся сразу после пробного периода'
          : 'Оплатите следующий месяц, чтобы кабинет не перешёл в режим просмотра',
        act: 'Оплатить',
        href: '/profile#billing',
        src: x,
      };
    }
    if (x.t === 'prog') {
      const r = daysBetween(T, x.till!);
      const who = x.kind === 'group' ? `участники группы (${x.members}) без тренировок` : 'клиент без тренировок';
      const tx =
        r < 0
          ? `Тренировки кончились ${-r} ${plural(-r, 'день', 'дня', 'дней')} назад — ${who}`
          : r === 0
            ? 'Сегодня последняя написанная тренировка'
            : `Написанные тренировки кончаются через ${r} ${plural(r, 'день', 'дня', 'дней')}`;
      return {
        key,
        t: x.t,
        d: x.d,
        ini: x.kind === 'group' ? grpIni(x.name) : ini(x.name),
        photo: x.kind === 'group' ? null : cl(x.id)?.photo,
        grp: x.kind === 'group',
        who: x.kind === 'group' ? '/groups?id=' + x.id : '/clients/' + x.id,
        title: x.kind === 'group' ? `Группа «${x.name}»` : x.name,
        tx,
        act: 'Составить',
        href: `/constructor?${x.kind === 'group' ? 'group' : 'client'}=${x.id}&date=${addDays(x.till!, 1)}`,
        src: x,
      };
    }
    const c = cl(x.clientId);
    const name = c?.name || 'Клиент';
    const base = { key, t: x.t, d: x.d, ini: ini(name), photo: c?.photo, who: '/clients/' + x.clientId, href: '/clients/' + x.clientId, src: x };
    switch (x.t) {
      case 'pr':
        return { ...base, title: name, tx: `Новый рекорд: ${pmLabel(x.key)} ${fmtNum(x.kg)} кг` + (x.prev != null ? ` (было ${fmtNum(x.prev)})` : ''), act: 'Пересчитать проценты' };
      case 'q': {
        const ex = x.exId ? cat.ex(x.exId)?.ru : null;
        const label = [x.blockTitle ? 'Блок «' + x.blockTitle + '»' : x.scope === 'day' ? 'Вся тренировка' : null, ex, 'тренировка ' + humanDate(x.date)].filter(Boolean).join(' · ');
        /* В просмотр тренировки (workout) к блоку вопроса — там вся переписка и ответ */
        const href = `/workout?client=${x.clientId}&date=${x.date}` + (x.blockId ? '#b' + x.blockId : '');
        return { ...base, title: name, tx: x.text, act: 'Ответить', qref: { label, href }, href };
      }
      case 'qa': {
        const ex = x.exId ? cat.ex(x.exId)?.ru : null;
        const label = [x.blockTitle ? 'Блок «' + x.blockTitle + '»' : x.scope === 'day' ? 'Вся тренировка' : null, ex, 'тренировка ' + humanDate(x.date)].filter(Boolean).join(' · ');
        /* В просмотр тренировки (workout) к блоку вопроса — там вся переписка и ответ */
        const href = `/workout?client=${x.clientId}&date=${x.date}` + (x.blockId ? '#b' + x.blockId : '');
        const tx = (
          <>
            {x.text}
            {x.answer ? <span className="fans">Вы: {x.answer}</span> : null}
          </>
        );
        return { ...base, title: name, tx, txt: x.text, act: 'Открыть тренировку', qref: { label, href }, href };
      }
      case 'miss':
        return { ...base, title: name, tx: `Не появлялся ${ago(x.d)} — серия прервана`, act: 'Открыть профиль' };
      case 'new':
        return { ...base, title: name, tx: c?.registered ? 'Пришёл по вашей ссылке — тренировок пока нет' : 'Тренировок пока нет', act: 'Составить тренировку', href: `/constructor?client=${x.clientId}&date=${T}` };
      case 'start':
        return {
          ...base,
          title: name,
          tx: `${x.she ? 'Прошла' : 'Прошёл'} первые шаги в приложении и получил${x.she ? 'а' : ''} достижение «Курс молодого бойца»: рекорды, замеры, фото профиля и первая тренировка`,
          act: 'Открыть профиль',
        };
      case 'done': {
        /* Как вопрос: ссылка на тренировку (к первому блоку с результатом) и что записано — по блокам и упражнениям */
        const she = c?.sex === 'f';
        const day = x.date === T ? 'сегодняшней тренировки' : 'тренировки ' + humanDate(x.date);
        const href = `/workout?client=${x.clientId}&date=${x.date}` + (x.blocks[0] ? '#b' + x.blocks[0].blockId : '');
        const label = (x.date === T ? 'Сегодняшняя тренировка' : 'Тренировка ' + humanDate(x.date)) + (x.finished ? ' · завершена' : '');
        const head = x.blocks.length ? `${she ? 'Записала' : 'Записал'} результаты ${day}` : `${she ? 'Завершила' : 'Завершил'} тренировку, результаты не ${she ? 'записывала' : 'записывал'}`;
        const MAX = 4;
        const rows: { k: string; b: string; t: string }[] = [];
        for (const bl of x.blocks) {
          if (bl.total) rows.push({ k: bl.blockId, b: bl.title, t: bl.total });
          bl.lines.forEach((l, i) => {
            const ex = (l.exId && cat.ex(l.exId)?.ru) || l.text || 'Упражнение';
            /* Блок «Рывок» с упражнением «Рывок» — без повтора */
            rows.push({ k: bl.blockId + i, b: bl.total || bl.title.toLowerCase() === ex.toLowerCase() ? ex : bl.title + ' · ' + ex, t: l.detail });
          });
        }
        const tx = (
          <>
            {head}
            {rows.length > 0 && (
              <span className="resl">
                {rows.slice(0, MAX).map((r) => (
                  <span key={r.k}>
                    <b>{r.b}</b> {r.t}
                  </span>
                ))}
                {rows.length > MAX && <span className="more">и ещё {rows.length - MAX}</span>}
              </span>
            )}
          </>
        );
        return { ...base, title: name, tx, txt: head + rows.map((r) => r.b + r.t).join(''), act: 'Ответить', qref: { label, href }, href };
      }
    }
  });
}

/** Аватар события: фото клиента, иначе инициалы; группа — значок */
function Ava({ e }: { e: Ev }) {
  if (e.grp || !e.ini)
    return (
      <span className="ava grp">
        <i>{e.grp ? <ICON.grp /> : <ICON.folder />}</i>
      </span>
    );
  return <span className="ava">{e.photo ? <img src={e.photo} alt="" /> : <i>{e.ini}</i>}</span>;
}

/* Событие (лента, вариант 2 прототипа): фото и имя ведут к клиенту, действие — ссылкой под текстом;
   на вопрос отвечают прямо здесь. */
function EvRow({ e }: { e: Ev }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const utils = trpc.useUtils();
  const send = trpc.trainer.send.useMutation({
    onSuccess: () => {
      toast('Ответ отправлен');
      setOpen(false);
      setText('');
      utils.trainer.feed.invalidate();
      utils.trainer.dashboard.invalidate();
      utils.trainer.clients.invalidate();
    },
    onError: (er) => toast(er.message || 'Не отправилось — попробуйте ещё раз'),
  });
  /* Ответ из ленты: на вопрос — туда же, к результатам — в обсуждение всей тренировки */
  const q = e.src.t === 'q' ? e.src : e.src.t === 'done' ? { clientId: e.src.clientId, date: e.src.date, scope: 'day' as const, refId: null } : null;
  const go = () => {
    const v = text.trim();
    if (!v || !q) return;
    send.mutate({ clientId: q.clientId, date: q.date, scope: q.scope, refId: q.refId, text: v });
  };
  return (
    <div className={`fev2 t-${e.t}`}>
      {e.who ? (
        <Link className="avl" to={e.who} title={e.title}>
          <Ava e={e} />
        </Link>
      ) : (
        <span className="avl">
          <Ava e={e} />
        </span>
      )}
      <div className="c">
        <div className="h">
          {e.who ? (
            <Link className="who" to={e.who}>
              {e.title}
            </Link>
          ) : (
            <b className="who">{e.title}</b>
          )}
          <span className={`tchip t-${e.t}`}>{TLABEL[e.t]}</span>
        </div>
        {e.qref && (
          <Link className="qref" to={e.qref.href}>
            <ICON.build />
            <span>{e.qref.label}</span>
          </Link>
        )}
        <div className="tx">
          {e.sub && <span className="ex">{e.sub}:</span>} {e.tx}
        </div>
        <div className="fa">
          {q ? (
            <>
              <button className="fact" onClick={() => setOpen(true)}>
                {e.act}
              </button>
              <Link className="fact" to={e.href}>
                Открыть тренировку
              </Link>
            </>
          ) : e.t === 'pr' ? (
            <button className="fact" onClick={() => toast('Проценты в назначенных тренировках считаются от нового рекорда')}>
              {e.act}
            </button>
          ) : (
            <Link className="fact" to={e.href}>
              {e.act}
            </Link>
          )}
        </div>
        {open && (
          <div className="reply">
            <input
              placeholder={e.t === 'done' ? 'Написать про тренировку…' : 'Ответить…'}
              autoFocus
              value={text}
              onChange={(x) => setText(x.target.value)}
              onKeyDown={(x) => {
                if (x.key === 'Enter') go();
                if (x.key === 'Escape') setOpen(false);
              }}
            />
            <button className="btn gh sm" disabled={send.isPending || !text.trim()} onClick={go}>
              Отправить
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Лента по дням: день — заголовком, события дня — в одной рамке */
export function FeedList({ list }: { list: Ev[] }) {
  if (!list.length)
    return (
      <div className="empty">
        <div className="t">Тихо</div>
        <div className="d">Новых событий нет</div>
      </div>
    );
  const T = today();
  const byDay: [string, Ev[]][] = [];
  for (const e of list) {
    const last = byDay[byDay.length - 1];
    if (last && last[0] === e.d) last[1].push(e);
    else byDay.push([e.d, [e]]);
  }
  return (
    <>
      {byDay.map(([d, evs]) => (
        <Fragment key={d}>
          <div className="evday2">
            {dayLabel(d)}
            {d === T || d === addDays(T, -1) ? ' · ' + humanDate(d) : ''}
          </div>
          <div className="evgrp">
            {evs.map((e) => (
              <EvRow key={e.key} e={e} />
            ))}
          </div>
        </Fragment>
      ))}
    </>
  );
}

/** Ключ события для счётчика «новых»: тип, клиент, день и начало текста */
export const evKey = (e: Ev) => [e.t, ('clientId' in e.src ? e.src.clientId : '') || '', e.d, typeof e.tx === 'string' ? e.tx.slice(0, 40) : (e.txt ?? e.title).slice(0, 80)].join('|');
